import type { CSSProperties } from 'react';
import { Info, CheckCircle, AlertTriangle, XCircle, X } from 'lucide-react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { cn } from '@/lib/utils';

/**
 * Alert component - displays an informational message with variant styling.
 */
export function Alert({ id, props, style }: SDUIComponentProps) {
  const {
    title,
    message,
    variant = 'info',
    dismissible = false,
    onDismiss,
    className,
  } = props as {
    title?: string;
    message: string;
    variant?: 'info' | 'success' | 'warning' | 'error';
    dismissible?: boolean;
    onDismiss?: () => void;
    className?: string;
  };

  const variantClasses = {
    info: {
      container: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
      icon: 'text-blue-500 dark:text-blue-400',
      title: 'text-blue-800 dark:text-blue-200',
      message: 'text-blue-700 dark:text-blue-300',
    },
    success: {
      container: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
      icon: 'text-green-500 dark:text-green-400',
      title: 'text-green-800 dark:text-green-200',
      message: 'text-green-700 dark:text-green-300',
    },
    warning: {
      container: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
      icon: 'text-yellow-500 dark:text-yellow-400',
      title: 'text-yellow-800 dark:text-yellow-200',
      message: 'text-yellow-700 dark:text-yellow-300',
    },
    error: {
      container: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
      icon: 'text-red-500 dark:text-red-400',
      title: 'text-red-800 dark:text-red-200',
      message: 'text-red-700 dark:text-red-300',
    },
  };

  const icons = {
    info: Info,
    success: CheckCircle,
    warning: AlertTriangle,
    error: XCircle,
  };

  const classes = variantClasses[variant];
  const IconComponent = icons[variant];

  return (
    <div
      id={id}
      className={cn('border rounded-md p-4', classes.container, className)}
      style={style as CSSProperties}
    >
      <div className="flex">
        <div className={cn('flex-shrink-0', classes.icon)}>
          <IconComponent className="h-5 w-5" />
        </div>
        <div className="ml-3 flex-1">
          {title && <h3 className={cn('text-sm font-medium', classes.title)}>{title}</h3>}
          <p className={cn('text-sm', title ? 'mt-1' : '', classes.message)}>{message}</p>
        </div>
        {dismissible && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onDismiss}
              className={cn(
                'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring',
                classes.icon,
              )}
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
